import { cutToast } from 'utility/toast';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import React, { useState } from 'react';

import {
  UserProfileUrgentUpdate,
  UserProfileUrgentUpdateVariables,
} from './__generated__/UserProfileUrgentUpdate';
import { useStyles } from './index.styles';
import { UserProfile } from '../__generated__/UserProfile';
import { UserProfileUrgentUpdateInput } from 'common/types/globalTypes';

import USER_SETUP_URGENCY_UPDATE from './UserProfileUrgentUpdate.graphql';
interface UrgencyFolderProps {
  userDetailsData?: UserProfile;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  userDetailsData,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(userDetailsData?.userProfile?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [userProfileUrgentUpdate] = useMutation<
    UserProfileUrgentUpdate,
    UserProfileUrgentUpdateVariables
  >(USER_SETUP_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      userDetailsData?.userProfile?._urgencyLevel === 0 ? 1 : 0;

    const { errors } = await userProfileUrgentUpdate({
      variables: {
        input: {
          entityId: userDetailsData?.userProfile?.id!,
          rowTimestamp: userDetailsData?.userProfile?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as UserProfileUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!userDetailsData?.userProfile?.id) {
          cache.modify({
            id: cache.identify({ ...userDetailsData.userProfile }),
            fields: {
              _urgencyLevel: () => {
                return data?.userProfileUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else {
      cutToast.success('Urgency status updated successfully.');
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
